/**
 * Local demo emulator rails (REACT_APP_USE_EMULATORS === 'true').
 * Recording uses the real product UI; the browser tab prefix is the warning.
 * Optional on-screen banner is off unless localStorage.VOLUNTAS_DEMO_BANNER === '1'.
 */
import { logger } from './logger';

export const DEMO_TITLE_PREFIX = 'DEMO — ';
export const DEMO_BANNER_STORAGE_KEY = 'VOLUNTAS_DEMO_BANNER';
export const DEMO_BANNER_ELEMENT_ID = 'voluntas-demo-emulator-banner';

let titleGuardInstalled = false;

export function isDemoEmulator(): boolean {
  return process.env.REACT_APP_USE_EMULATORS === 'true';
}

export function assertDemoBlocksLiveOutbound(action: string): void {
  if (!isDemoEmulator()) return;
  throw new Error(`Demo emulator — ${action} is disabled.`);
}

function withDemoTitlePrefix(title: string): string {
  const value = title || 'VOLOCEAN';
  if (value.startsWith(DEMO_TITLE_PREFIX)) return value;
  return `${DEMO_TITLE_PREFIX}${value}`;
}

function readDocumentTitleDescriptor(): PropertyDescriptor | undefined {
  return (
    Object.getOwnPropertyDescriptor(Document.prototype, 'title') ||
    Object.getOwnPropertyDescriptor(HTMLDocument.prototype, 'title')
  );
}

function installDemoTitleSetter(): void {
  const descriptor = readDocumentTitleDescriptor();
  if (!descriptor?.configurable || !descriptor.get || !descriptor.set) {
    logger.warn('[demoEmulator] Could not wrap document.title; MutationObserver will still prefix');
    return;
  }

  const originalGet = descriptor.get;
  const originalSet = descriptor.set;
  Object.defineProperty(document, 'title', {
    configurable: true,
    enumerable: descriptor.enumerable,
    get() {
      return originalGet.call(this);
    },
    set(value: string) {
      originalSet.call(this, withDemoTitlePrefix(String(value ?? '')));
    }
  });
}

function installDemoTitleObserver(): void {
  const titleEl = document.querySelector('title');
  if (!titleEl) return;

  const observer = new MutationObserver(() => {
    const next = withDemoTitlePrefix(document.title);
    if (document.title !== next) {
      document.title = next;
    }
  });
  observer.observe(titleEl, { childList: true, characterData: true, subtree: true });

  const head = document.head;
  if (!head) return;
  const headObserver = new MutationObserver(() => {
    const current = document.querySelector('title');
    if (current && current !== titleEl) {
      observer.observe(current, { childList: true, characterData: true, subtree: true });
    }
    const next = withDemoTitlePrefix(document.title);
    if (document.title !== next) {
      document.title = next;
    }
  });
  headObserver.observe(head, { childList: true });
}

export function mountOptionalDemoEmulatorBanner(): void {
  if (!isDemoEmulator() || typeof document === 'undefined' || typeof window === 'undefined') {
    return;
  }
  if (window.localStorage.getItem(DEMO_BANNER_STORAGE_KEY) !== '1') return;
  if (document.getElementById(DEMO_BANNER_ELEMENT_ID)) return;

  const banner = document.createElement('div');
  banner.id = DEMO_BANNER_ELEMENT_ID;
  banner.setAttribute('role', 'status');
  banner.className =
    'fixed top-0 inset-x-0 z-[9999] px-3 py-1 text-center text-xs font-medium bg-warning-50 text-warning-800 border-b border-warning-200';
  banner.textContent = 'Local demo emulator — not production';
  document.body.appendChild(banner);
}

export function applyDemoDocumentTitle(): void {
  if (!isDemoEmulator() || typeof document === 'undefined') return;

  if (!titleGuardInstalled) {
    titleGuardInstalled = true;
    installDemoTitleSetter();
    installDemoTitleObserver();
  }

  const next = withDemoTitlePrefix(document.title);
  if (document.title !== next) {
    document.title = next;
  }

  mountOptionalDemoEmulatorBanner();
}
